import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { searchSkuLocations, checkOutLabel, checkInLabel, getShelfIndexHistory, setPrimaryLocation, getLabelStatus } from '../../services/shelfIndexAPI';
import { useHybridShelfIndexSync } from '../../hooks/useHybridShelfIndexSync';
import { useReprintByPrefix } from '../../hooks/useReprintByPrefix';
import { printRunProgressMessage } from '../../hooks/usePrintOrderRun';
import { getApiErrorMessage } from '../../services/errorUtils';
import { formatType, areLocationRowsEqual } from '../../utils/shelfIndexUtils';
import { formatCreatedAt } from '../../utils/formatCreatedAt';
import '../warehouse/SearchWarehouse.css';
import '../labels/PrintBoxLabel.css';
import '../receiving/PrintLabelModal.css';
import ShelfIndexer from '../labels/ShelfIndexer';
import LabelDetailsPanel, { formatHistoryDate, type ShelfIndexHistoryRow } from './LabelDetailsPanel';
import BundleBinQuantityModal from './BundleBinQuantityModal';
import SuccessFlash, { useSuccessFlash } from './SuccessFlash';
import { useBundleFloorLevel } from '../../hooks/useBundleFloorLevel';
import type { BundleFloorBinLevel, BundleFloorLevel } from '../../schemas/apiResponse.schemas';

const STOCK_COLLAPSE_THRESHOLD = 3;

interface LocationRow {
  id: string;
  sku?: string;
  shelf_index: string;
  type: string;
  is_primary?: boolean;
}

interface SingleDisplayRow {
  type: 'single';
  loc: LocationRow;
}

interface CollapsedDisplayRow {
  type: 'collapsed';
  key: string;
  shelfIndex: string;
  locs: LocationRow[];
  count: number;
}

type DisplayRow = SingleDisplayRow | CollapsedDisplayRow;

function isStockType(type: string | undefined | null): boolean {
  return !!type && String(type).toLowerCase().trim() === 'stock';
}

function buildDisplayRows(locations: LocationRow[]): DisplayRow[] {
  const rows: DisplayRow[] = [];
  const stockByShelf: Record<string, LocationRow[]> = {};

  for (const loc of locations) {
    if (isStockType(loc.type)) {
      const key = `${loc.shelf_index}|stock`;
      if (!stockByShelf[key]) stockByShelf[key] = [];
      stockByShelf[key].push(loc);
    } else {
      rows.push({ type: 'single', loc });
    }
  }

  const stockKeys = Object.keys(stockByShelf).sort();
  for (const key of stockKeys) {
    const locs = stockByShelf[key];
    const shelfIndex = locs[0]?.shelf_index ?? '';
    if (locs.length > STOCK_COLLAPSE_THRESHOLD) {
      rows.push({ type: 'collapsed', key, shelfIndex, locs, count: locs.length });
    } else {
      for (const loc of locs) rows.push({ type: 'single', loc });
    }
  }

  return rows;
}

interface LabelCheckOutModalProps {
  loc: LocationRow;
  sku: string;
  onClose: () => void;
  onSuccess: () => void;
  checkedIn?: boolean;
  /** Called after Update bin quantity saves, so a parent table can show the new count. */
  onBundleFloorUpdated?: (floor: BundleFloorLevel) => void;
}

function extractStorageIdPrefix(id: string): string {
  return id.replace(/-/g, '').substring(0, 6).toLowerCase();
}

interface ReprintOverlayProps {
  storageIdPrefix: string;
  onClose: () => void;
}

interface ShelfIndexerOverlayProps {
  storageIdPrefix: string;
  allowCheckedOut: boolean;
  onClose: () => void;
}

function ShelfIndexerOverlay({ storageIdPrefix, allowCheckedOut, onClose }: ShelfIndexerOverlayProps) {
  const initialLoad = useMemo(
    () => ({ storageIdPrefix, allowCheckedOut }),
    [storageIdPrefix, allowCheckedOut],
  );
  return (
    <div
      className="plm-overlay sw-shelf-indexer-reindex-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      role="presentation"
    >
      <button
        type="button"
        className="plm-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
      >
        ×
      </button>
      <div onClick={(e) => e.stopPropagation()}>
        <ShelfIndexer embedded initialLoad={initialLoad} />
      </div>
    </div>
  );
}

function ReprintOverlay({ storageIdPrefix, onClose }: ReprintOverlayProps) {
  const [labelCount, setLabelCount] = useState(1);
  const [bigLabelCount, setBigLabelCount] = useState(1);
  const [reprintMessage, setReprintMessage] = useState('');
  const { reprint, isRunning: loading } = useReprintByPrefix('reprint_panel');

  const runReprint = useCallback(
    async (isBigLabel: boolean, copies: number) => {
      if (loading) return;
      setReprintMessage(copies > 1 ? `Printing ${copies} labels...` : 'Printing...');
      const result = await reprint({
        storageIdPrefix,
        isBigLabel,
        copies,
        onProgress: (progress) => {
          setReprintMessage(printRunProgressMessage(progress));
        },
      });
      setReprintMessage(result.message);
      setTimeout(() => setReprintMessage(''), result.completed ? 3000 : 10000);
    },
    [reprint, storageIdPrefix, loading],
  );

  const handleReprint = useCallback(() => { void runReprint(false, labelCount); }, [runReprint, labelCount]);
  const handleReprintBig = useCallback(() => { void runReprint(true, bigLabelCount); }, [runReprint, bigLabelCount]);

  return (
    <div className="plm-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }} role="presentation">
      <button className="plm-close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close">×</button>
      <div className="pbl-container" onClick={(e) => e.stopPropagation()}>
        <h1 className="pbl-title">Reprint Label</h1>
        <div className="pbl-section">
          <div className="pbl-section-title pbl-section-info">
            <span className="pbl-section-text">Storage ID</span>
          </div>
          <div className="pbl-info-display">
            <div className="pbl-info-row">
              <span className="pbl-info-label">Storage ID:</span>
              <span className="pbl-info-value">{storageIdPrefix}</span>
            </div>
          </div>
        </div>
        {reprintMessage && <div className="pbl-message">{reprintMessage}</div>}
        <div className="pbl-submit-section">
          <div className="pbl-section-title pbl-section-info">
            <span className="pbl-section-text">Print Stock Labels</span>
          </div>
          <div className="pbl-print-buttons">
            <div className="pbl-count-row">
              <button
                type="button"
                className="pbl-increment-btn"
                onClick={() => setLabelCount((c) => Math.max(1, c - 1))}
                disabled={loading || labelCount <= 1}
                aria-label="Decrease label count"
              >
                −
              </button>
              <button
                type="button"
                onClick={handleReprint}
                disabled={loading}
                className="pbl-print-button pbl-print-button-in-row"
              >
                {loading ? 'Printing...' : `Print ${labelCount} Label${labelCount !== 1 ? 's' : ''}`}
              </button>
              <button
                type="button"
                className="pbl-increment-btn"
                onClick={() => setLabelCount((c) => Math.min(8, c + 1))}
                disabled={loading || labelCount >= 8}
                aria-label="Increase label count"
              >
                +
              </button>
            </div>
            <div className="pbl-count-row">
              <button
                type="button"
                className="pbl-increment-btn"
                onClick={() => setBigLabelCount((c) => Math.max(1, c - 1))}
                disabled={loading || bigLabelCount <= 1}
                aria-label="Decrease big label count"
              >
                −
              </button>
              <button
                type="button"
                onClick={handleReprintBig}
                disabled={loading}
                className="pbl-print-big-button pbl-print-button-in-row"
              >
                {loading ? 'Printing...' : `Print ${bigLabelCount} Big Label${bigLabelCount !== 1 ? 's' : ''}`}
              </button>
              <button
                type="button"
                className="pbl-increment-btn"
                onClick={() => setBigLabelCount((c) => Math.min(8, c + 1))}
                disabled={loading || bigLabelCount >= 8}
                aria-label="Increase big label count"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LabelCheckOutModal({
  loc,
  sku,
  onClose,
  onSuccess,
  checkedIn = false,
  onBundleFloorUpdated,
}: LabelCheckOutModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState<ShelfIndexHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showReprint, setShowReprint] = useState(false);
  const [showReindex, setShowReindex] = useState(false);
  const [showBinQty, setShowBinQty] = useState(false);
  const [binQtyMessage, setBinQtyMessage] = useState('');
  const binQtyFlash = useSuccessFlash();
  const [createdAtDisplay, setCreatedAtDisplay] = useState('');
  const [statusCheckedIn, setStatusCheckedIn] = useState<boolean | null>(null);
  const { floor: bundleFloor, applyFloorUpdate } = useBundleFloorLevel(sku);
  const labelPrefix = loc?.id ? extractStorageIdPrefix(loc.id) : '';
  const bundleBin = bundleFloor?.bins?.find((bin) => bin.storageIdPrefix.toLowerCase() === labelPrefix) ?? null;

  useEffect(() => {
    if (!loc?.id) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistory([]);
    getShelfIndexHistory(loc.id).then((rows: ShelfIndexHistoryRow[]) => {
      if (!cancelled) {
        setHistory(rows);
        setHistoryLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setHistory([]);
        setHistoryLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [loc?.id]);

  useEffect(() => {
    if (!loc?.id) {
      setCreatedAtDisplay('');
      setStatusCheckedIn(null);
      return;
    }
    let cancelled = false;
    setCreatedAtDisplay('');
    setStatusCheckedIn(null);
    getLabelStatus(extractStorageIdPrefix(loc.id)).then((status) => {
      if (cancelled) return;
      if (status.found) {
        setStatusCheckedIn(status.checkedIn);
        if (status.createdAt) {
          setCreatedAtDisplay(formatCreatedAt(status.createdAt));
        }
      }
    }).catch(() => {
      if (!cancelled) {
        setCreatedAtDisplay('');
        setStatusCheckedIn(null);
      }
    });
    return () => { cancelled = true; };
  }, [loc?.id]);

  const handleCheckOut = useCallback(async () => {
    setSubmitting(true);
    setMessage('');
    try {
      const result = await checkOutLabel(loc.id);
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setMessage(result.message || 'Failed to check out label');
      }
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err, 'Failed to check out label'));
    } finally {
      setSubmitting(false);
    }
  }, [loc?.id, onClose, onSuccess]);

  const handleCheckIn = useCallback(async () => {
    setSubmitting(true);
    setMessage('');
    try {
      const result = await checkInLabel(loc.id);
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setMessage(result.message || 'Failed to check in label');
      }
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err, 'Failed to check in label'));
    } finally {
      setSubmitting(false);
    }
  }, [loc?.id, onClose, onSuccess]);

  if (!loc) return null;

  return (
    <div
      className="pbl-modal-overlay sw-label-checkout-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div className="pbl-modal sw-label-checkout-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <LabelDetailsPanel
          storageIdPrefix={loc.id}
          createdAtDisplay={createdAtDisplay}
          /* Modal `checkedIn` means “needs Check in” (label is out); API `checkedIn` is warehouse-in. */
          checkedIn={statusCheckedIn ?? !checkedIn}
          sku={sku}
          shelfIndex={loc.shelf_index}
          type={loc.type}
        />
        <div className="sw-label-checkout-history">
          <div className="sw-label-checkout-history-title">Shelf index history</div>
          {historyLoading ? (
            <div className="sw-label-checkout-history-loading">Loading...</div>
          ) : history.length === 0 ? (
            <div className="sw-label-checkout-history-empty">No history</div>
          ) : (
            <table className="sw-label-checkout-history-table">
              <thead>
                <tr>
                  <th>Shelf</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row, idx) => (
                  <tr key={`${row.created_at}-${idx}`}>
                    <td>{row.shelf_index ?? '—'}</td>
                    <td>{formatHistoryDate(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {message && <div className="sw-label-checkout-message">{message}</div>}
        {binQtyMessage && (
          <div className="sw-label-checkout-message sw-label-checkout-message-success" role="status">
            {binQtyMessage}
          </div>
        )}
        <div className="sw-label-checkout-actions">
          <button
            type="button"
            className="sw-label-checkout-btn sw-label-reprint-btn"
            onClick={() => setShowReprint(true)}
          >
            Reprint
          </button>
          <button
            type="button"
            className="sw-label-checkout-btn sw-label-reindex-btn"
            onClick={() => setShowReindex(true)}
          >
            Re-index
          </button>
          {bundleBin ? (
            <button
              type="button"
              className="sw-label-checkout-btn sw-label-bin-qty-btn"
              onClick={() => {
                setBinQtyMessage('');
                setShowBinQty(true);
              }}
            >
              Update bin quantity
            </button>
          ) : null}
          {checkedIn ? (
            <button
              type="button"
              className="sw-label-checkout-btn"
              onClick={handleCheckIn}
              disabled={submitting}
            >
              {submitting ? 'Checking in...' : 'Check in'}
            </button>
          ) : (
            <button
              type="button"
              className="sw-label-checkout-btn"
              onClick={handleCheckOut}
              disabled={submitting}
            >
              {submitting ? 'Checking out...' : 'Check out'}
            </button>
          )}
        </div>
      </div>

      {showReprint && (
        <ReprintOverlay
          storageIdPrefix={extractStorageIdPrefix(loc.id)}
          onClose={() => setShowReprint(false)}
        />
      )}
      {showReindex && (
        <ShelfIndexerOverlay
          storageIdPrefix={extractStorageIdPrefix(loc.id)}
          allowCheckedOut={checkedIn}
          onClose={() => setShowReindex(false)}
        />
      )}
      {showBinQty && bundleBin && bundleFloor && (
        <BundleBinQuantityModal
          sku={bundleFloor.sku}
          bin={bundleBin}
          onClose={() => setShowBinQty(false)}
          onUpdated={(updated, quantity) => {
            applyFloorUpdate(updated);
            onBundleFloorUpdated?.(updated);
            setShowBinQty(false);
            setBinQtyMessage(`Bin quantity set to ${quantity}`);
            binQtyFlash.flash();
          }}
        />
      )}
      <SuccessFlash visible={binQtyFlash.visible} />
    </div>
  );
}

export { LabelCheckOutModal };

interface PrimaryConfirmPopupProps {
  loc: LocationRow;
  onConfirm: () => void;
  onCancel: () => void;
  submitting: boolean;
  message: string;
}

function PrimaryConfirmPopup({ loc, onConfirm, onCancel, submitting, message }: PrimaryConfirmPopupProps) {
  return (
    <div className="pbl-modal-overlay" onClick={onCancel} role="presentation">
      <div className="pbl-modal sw-primary-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onCancel} aria-label="Close">
          ×
        </button>
        <h3 className="sw-primary-confirm-title">Change primary pick location?</h3>
        <div className="sw-primary-confirm-info">
          <span>Shelf: <strong>{loc.shelf_index}</strong></span>
          <span>ID: <strong>{loc.id}</strong></span>
          <span>Type: <strong>{formatType(loc.type)}</strong></span>
        </div>
        {message && <div className="sw-primary-confirm-message">{message}</div>}
        <div className="sw-primary-confirm-actions">
          <button
            type="button"
            className="sw-primary-confirm-btn sw-primary-confirm-no"
            onClick={onCancel}
            disabled={submitting}
          >
            No
          </button>
          <button
            type="button"
            className="sw-primary-confirm-btn sw-primary-confirm-yes"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? 'Updating...' : 'Yes'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface StorageInfoModalProps {
  sku: string;
  title?: string;
}

function StorageInfoModal({ sku, title }: StorageInfoModalProps) {
  const [skuLocations, setSkuLocations] = useState<LocationRow[]>([]);
  const [loading, setSkuLocationsLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [selectedLoc, setSelectedLoc] = useState<LocationRow | null>(null);
  const [primaryCandidate, setPrimaryCandidate] = useState<LocationRow | null>(null);
  const [primarySubmitting, setPrimarySubmitting] = useState(false);
  const [primaryMessage, setPrimaryMessage] = useState('');
  const { floor: bundleFloor, applyFloorUpdate } = useBundleFloorLevel(sku);
  const [binQtyTarget, setBinQtyTarget] = useState<BundleFloorBinLevel | null>(null);
  const binQtyFlash = useSuccessFlash();
  const binByPrefix = useMemo(() => {
    const map = new Map<string, BundleFloorBinLevel>();
    bundleFloor?.bins?.forEach((bin) => map.set(bin.storageIdPrefix.toLowerCase(), bin));
    return map;
  }, [bundleFloor]);
  const showQuantity = binByPrefix.size > 0;
  const quantityCell = (loc: LocationRow): React.ReactNode => {
    if (!showQuantity) return null;
    const bin = binByPrefix.get(extractStorageIdPrefix(loc.id));
    if (!bin) return <td>—</td>;
    const open = (e: React.SyntheticEvent): void => {
      e.stopPropagation();
      setBinQtyTarget(bin);
    };
    return (
      <td
        role="button"
        tabIndex={0}
        aria-label={`Update bin quantity for ${bin.shelfIndex ?? bin.storageIdPrefix}, now ${bin.quantity}`}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open(e);
          }
        }}
      >
        {bin.quantity}
      </td>
    );
  };

  const refreshLocations = useCallback(async (source = 'unknown') => {
    if (!sku) {
      setSkuLocations([]);
      setSkuLocationsLoading(false);
      return;
    }
    const showLoading = !String(source).startsWith('hybrid-');
    if (showLoading) {
      setSkuLocationsLoading(true);
    }
    try {
      const result = await searchSkuLocations(sku);
      if (result.error) {
        setSkuLocations([]);
      } else {
        const nextLocations: LocationRow[] = result.locations || [];
        setSkuLocations((prev) => (areLocationRowsEqual(prev, nextLocations) ? prev : nextLocations));
      }
    } catch {
      setSkuLocations([]);
    } finally {
      if (showLoading) {
        setSkuLocationsLoading(false);
      }
    }
  }, [sku]);

  useEffect(() => {
    refreshLocations('initial');
  }, [refreshLocations]);

  useHybridShelfIndexSync({
    enabled: Boolean(sku),
    onSync: (source) => refreshLocations(`hybrid-${source}`),
    pollMs: 20000,
  });

  const toggleExpanded = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleIdClick = useCallback((e: React.MouseEvent, loc: LocationRow) => {
    e.stopPropagation();
    setSelectedLoc(loc);
  }, []);

  const handleCloseLabelModal = useCallback(() => {
    setSelectedLoc(null);
  }, []);

  const handleRowClick = useCallback((loc: LocationRow) => {
    if (loc.is_primary) return;
    setPrimaryCandidate(loc);
    setPrimaryMessage('');
  }, []);

  const handlePrimaryConfirm = useCallback(async () => {
    if (!primaryCandidate || !sku) return;
    setPrimarySubmitting(true);
    setPrimaryMessage('');
    try {
      const result = await setPrimaryLocation(sku, primaryCandidate.id);
      if (result.success) {
        setPrimaryCandidate(null);
        refreshLocations('primary-update');
      } else {
        setPrimaryMessage(result.message || 'Failed to update primary location');
      }
    } catch {
      setPrimaryMessage('Failed to update primary location');
    } finally {
      setPrimarySubmitting(false);
    }
  }, [primaryCandidate, sku, refreshLocations]);

  const handlePrimaryCancel = useCallback(() => {
    setPrimaryCandidate(null);
    setPrimaryMessage('');
  }, []);

  if (!sku) return null;

  const displayRows = buildDisplayRows(skuLocations);

  return (
    <div className="sw-results-section">
      <div className="sw-results-title">{title ?? 'SKU Locations'}</div>
      {loading ? (
        <div className="sw-results-empty">Loading...</div>
      ) : skuLocations.length === 0 ? (
        <div className="sw-results-empty">No locations found</div>
      ) : (
        <div className="sw-table-scroll">
        <table className="sw-results-table sw-results-id-type-shelf">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Shelf</th>
              {showQuantity ? <th>Quantity</th> : null}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => {
              if (row.type === 'single') {
                return (
                  <tr
                    key={`${row.loc.shelf_index}-${row.loc.id}-${idx}`}
                    className={row.loc.is_primary ? 'sw-results-row-primary' : 'sw-results-row-clickable'}
                    onClick={() => handleRowClick(row.loc)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(row.loc);
                      }
                    }}
                  >
                    <td>
                      <button
                        type="button"
                        className="sw-results-id-clickable"
                        onClick={(e) => handleIdClick(e, row.loc)}
                      >
                        {row.loc.id}
                      </button>
                    </td>
                    <td>{formatType(row.loc.type)}</td>
                    <td>{row.loc.shelf_index}</td>
                    {quantityCell(row.loc)}
                  </tr>
                );
              }
              const isExpanded = expandedKeys.has(row.key);
              return (
                <React.Fragment key={row.key}>
                  <tr
                    className="sw-results-row-expandable"
                    onClick={() => toggleExpanded(row.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded(row.key);
                      }
                    }}
                    aria-expanded={isExpanded}
                  >
                    <td>
                      <span className="sw-results-collapsed-id">
                        {row.count} boxes {isExpanded ? '▾' : '▸'}
                      </span>
                    </td>
                    <td>{formatType('stock')}</td>
                    <td>{row.shelfIndex}</td>
                    {showQuantity ? <td>—</td> : null}
                  </tr>
                  {isExpanded &&
                    row.locs.map((loc, i) => (
                      <tr
                        key={`${row.key}-${loc.id}-${i}`}
                        className={`sw-results-row-expanded ${loc.is_primary ? 'sw-results-row-primary' : 'sw-results-row-clickable'}`}
                        onClick={() => handleRowClick(loc)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowClick(loc);
                          }
                        }}
                      >
                        <td>
                          <button
                            type="button"
                            className="sw-results-id-clickable"
                            onClick={(e) => handleIdClick(e, loc)}
                          >
                            {loc.id}
                          </button>
                        </td>
                        <td>{formatType(loc.type)}</td>
                        <td>{loc.shelf_index}</td>
                        {quantityCell(loc)}
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {selectedLoc && (
        <LabelCheckOutModal
          loc={selectedLoc}
          sku={sku}
          onClose={handleCloseLabelModal}
          onSuccess={refreshLocations}
          onBundleFloorUpdated={applyFloorUpdate}
        />
      )}

      {binQtyTarget && bundleFloor && (
        <BundleBinQuantityModal
          sku={bundleFloor.sku}
          bin={binQtyTarget}
          onClose={() => setBinQtyTarget(null)}
          onUpdated={(updated) => {
            applyFloorUpdate(updated);
            setBinQtyTarget(null);
            binQtyFlash.flash();
          }}
        />
      )}
      <SuccessFlash visible={binQtyFlash.visible} />

      {primaryCandidate && (
        <PrimaryConfirmPopup
          loc={primaryCandidate}
          onConfirm={handlePrimaryConfirm}
          onCancel={handlePrimaryCancel}
          submitting={primarySubmitting}
          message={primaryMessage}
        />
      )}
    </div>
  );
}

export default StorageInfoModal;
